// verdict.js — paper 전략 자동 판정(Verdict).
//
// 판단 로직의 단일 소스는 백엔드다: paper-trader `/api/summary` 가 전략·family
// 마다 `verdict { label, score, gates }` 를 응답한다(internal/web/verdict.go).
// 프론트는 그 값을 렌더만 한다(readVerdict).
//
// computeVerdict 는 백엔드 응답에 verdict 가 아직 없을 때(구버전 배포 전 / 프론트
// 에서 합산한 그룹)만 쓰는 폴백이며, Go 게이트 기준을 그대로 미러링한다:
//
//   g1 표본    n ≥ 30
//   g2 PF      ≥ 1.0  (GO 승격 1.3)   · PF N/A(무손실)+순익>0 = 통과
//   g3 승률    ≥ 30%  (GO 승격 40%)
//   g4 순익    > 0
//   g5 holdout 운영기간 ≥ 14일
//
//   DORMANT  거래 < 5
//   GO       n≥30 ∧ PF≥1.3 ∧ WR≥40% ∧ pnl>0
//   NO-GO    n≥10 ∧ (PF<1.0 ∨ WR<30% ∨ pnl<0)
//   CONTINUE 그 외

export const VERDICT_GO = "go";
export const VERDICT_CONTINUE = "continue";
export const VERDICT_NOGO = "nogo";
export const VERDICT_DORMANT = "dormant";

/**
 * 판정순 정렬 랭크 — 실전 투입 후보(GO)가 맨 위, 그다음 관찰 대상(검증중),
 * 폐기 후보(NO-GO), 판단 보류(데이터 부족) 순. (DAS-142 판정순 정렬)
 */
export const VERDICT_RANK = {
    [VERDICT_GO]: 0,
    [VERDICT_CONTINUE]: 1,
    [VERDICT_NOGO]: 2,
    [VERDICT_DORMANT]: 3,
};

/** PF 게이트 — ProfitFct<0 은 "손실 거래 없음"(N/A) → 순익이 양이면 통과. */
function pfOK(pf, pnl, thr) {
    if (pf < 0) return pnl > 0;
    return pf >= thr;
}

/** 첫 거래 이후 경과 일수(없으면 0). */
export function daysInPaper(firstTradeTs, now = Date.now()) {
    if (!firstTradeTs) return 0;
    const t = new Date(firstTradeTs).getTime();
    if (!Number.isFinite(t)) return 0;
    const d = Math.floor((now - t) / 86400000);
    return d > 0 ? d : 0;
}

/**
 * computeVerdict — 폴백 판정 계산.
 * @param {{trades:number, pf:number, wr:number, pnl:number, days:number}} f
 * @returns {{label:string, score:string, gates:Array}}
 */
export function computeVerdict({ trades = 0, pf = -1, wr = 0, pnl = 0, days = 0 } = {}) {
    const na = trades < 5;
    const gates = [
        { key: "표본 n≥30", pass: trades >= 30, na, val: `n=${trades}` },
        {
            key: "PF≥1.0",
            pass: pfOK(pf, pnl, 1.0),
            na,
            val: pf < 0 ? "PF N/A" : `PF ${pf.toFixed(2)}`,
        },
        { key: "WR≥30%", pass: wr >= 0.3, na, val: `WR ${(wr * 100).toFixed(0)}%` },
        { key: "순익>0", pass: pnl > 0, na, val: `${pnl >= 0 ? "+" : ""}${Math.round(pnl)}` },
        { key: "holdout 14일", pass: days >= 14, na: false, val: `${days}d` },
    ];

    let label = VERDICT_CONTINUE;
    if (trades < 5) {
        label = VERDICT_DORMANT;
    } else if (trades >= 30 && pfOK(pf, pnl, 1.3) && wr >= 0.4 && pnl > 0) {
        label = VERDICT_GO;
    } else if (trades >= 10 && (!pfOK(pf, pnl, 1.0) || wr < 0.3 || pnl < 0)) {
        label = VERDICT_NOGO;
    }

    let score = "—";
    if (!na) {
        let pass = 0;
        let tot = 0;
        for (const g of gates) {
            if (g.na) continue;
            tot += 1;
            if (g.pass) pass += 1;
        }
        score = `${pass}/${tot}`;
    }
    return { label, score, gates };
}

/**
 * verdictLabelOf — 전략(단일/그룹)의 판정 라벨 1개를 구한다.
 * 그룹은 백엔드 family verdict(단일 소스)가 있으면 그것을 우선하고,
 * 없으면 readVerdict 폴백. 판정 필터·판정순 정렬에서 사용 (DAS-142).
 *
 * @param {object} s summary.strategies[i] 또는 groupStrategies 그룹
 * @param {Map<string, {label:string}>} [familyVerdictMap] family_id → verdict
 * @param {object} [fields] readStrategyFields 결과(폴백 계산용)
 * @returns {string} go | continue | nogo | dormant
 */
export function verdictLabelOf(s, familyVerdictMap, fields) {
    if (s?.isGroup && familyVerdictMap instanceof Map) {
        const v = familyVerdictMap.get(s.baseId);
        if (v && v.label) return v.label;
    }
    return readVerdict(s, fields)?.label ?? VERDICT_DORMANT;
}

/**
 * readVerdict — 백엔드 verdict 우선, 없으면 stats 필드로 폴백 계산.
 * @param {object} s summary.strategies[i] | families[i] | groupStrategies 그룹 | 단일
 * @param {object} [fields] readStrategyFields 결과(있으면 폴백에 사용)
 * @returns {{label:string, score:string, gates?:Array}}
 */
export function readVerdict(s, fields) {
    const v = s?.verdict ?? s?.Verdict;
    if (v && v.label) return v;
    const f = fields
        ? {
              trades: fields.trades,
              pf: fields.pf,
              wr: fields.wr,
              pnl: fields.pnl,
              days: daysInPaper(fields.firstTradeTs),
          }
        : {
              trades: s?.trades ?? s?.Trades ?? 0,
              pf: s?.profit_factor ?? s?.ProfitFct ?? -1,
              wr: s?.win_rate ?? s?.WinRate ?? 0,
              pnl: s?.pnl_krw ?? s?.PnLKRW ?? 0,
              days: daysInPaper(s?.first_trade_ts ?? s?.FirstTradeTs),
          };
    return computeVerdict(f);
}
